import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAlertEmail, isAlertPhone } from './alertSignup';
import { sensorPlace } from './levelStatus';
import { saveAlertSubscription } from './sensorData';

export const HowItWorks = () => (
  <main className="page article">
    <h1>How it works</h1>
    <p>
      Flood Center reads water level from sensors in canals and creeks.
      Each sensor reports about every 5 minutes.
    </p>
    <h2>What the colors mean</h2>
    <p>
      Normal means the water is below that sensor’s watch level.
      Watch means the water has reached the watch level.
      Flooding means the water has reached the flood level.
      A grey marker means the sensor has not reported in the last 6 hours,
      so the last number is not treated as the current condition.
    </p>
    <h2>Rain</h2>
    <p>
      Rain for the next 6 hours is a forecast. Rain for past periods uses the
      rainfall forecasts already stored for those times, which is what the
      previous charts showed. It is not a gauge at the sensor.
    </p>
    <p>
      Switch between metric and US units under Sensor details on a sensor’s full history.
      Water level is shown in meters or feet. Rain is shown in millimeters or inches.
    </p>
  </main>
);

const signupErrorMessage = (err) => {
  if (err?.code === 'invalid-email') return err.message;
  if (err?.code === 'invalid-phone') return err.message;
  if (err?.code === 'invalid-sensor') return err.message;
  if (err?.code === 'permission-denied') {
    return 'The address could not be saved. Alert signup is not open on the server yet.';
  }
  return 'The address could not be saved. Try again in a moment.';
};

export const AlertsPage = ({ sensors = [] }) => {
  const [params] = useSearchParams();
  const preset = params.get('sensor') || 'all';
  const [channel, setChannel] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [deviceId, setDeviceId] = useState(preset);
  const [saving, setSaving] = useState(false);
  const [savedContact, setSavedContact] = useState('');
  const [savedChannel, setSavedChannel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setDeviceId(preset);
  }, [preset]);

  const knownIds = new Set(sensors.map((sensor) => sensor.id));
  const options = sensors.map((sensor) => {
    const place = sensorPlace(sensor, sensor.id);
    const label = place.deviceId && place.deviceId !== place.title
      ? `${place.title} (${place.deviceId})`
      : place.title;
    return { id: sensor.id, label };
  });
  if (preset !== 'all' && !knownIds.has(preset)) {
    options.unshift({ id: preset, label: sensorPlace(null, preset).title });
  }

  const contactReady = channel === 'text' ? phone.trim() : email.trim();

  const submit = async (event) => {
    event.preventDefault();
    if (channel === 'text' && !isAlertPhone(phone)) {
      setError('Enter a valid mobile number.');
      setSavedContact('');
      return;
    }
    if (channel === 'email' && !isAlertEmail(email)) {
      setError('Enter a valid email address.');
      setSavedContact('');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveAlertSubscription(channel === 'text'
        ? { phone, deviceId }
        : { email, deviceId });
      setSavedContact(channel === 'text' ? phone.trim() : email.trim());
      setSavedChannel(channel);
      setEmail('');
      setPhone('');
    } catch (err) {
      console.error('[alerts]', err);
      setSavedContact('');
      setError(signupErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="page article">
      <h1>Get alerts</h1>
      <p>
        Choose text or email to be notified when a sensor reaches Watch or Flooding.
        The address or number is stored now. Messages are sent once alert delivery is turned on.
      </p>
      <form className="alert-form" onSubmit={submit}>
        <div className="segmented alert-channel" role="group" aria-label="How to send alerts">
          <button
            type="button"
            aria-pressed={channel === 'text'}
            onClick={() => {
              setChannel('text');
              setError('');
            }}
          >
            Text
          </button>
          <button
            type="button"
            aria-pressed={channel === 'email'}
            onClick={() => {
              setChannel('email');
              setError('');
            }}
          >
            Email
          </button>
        </div>
        {channel === 'text' ? (
          <label>
            Mobile number
            <input
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              required
              value={phone}
              placeholder="(415) 555-0100"
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
        ) : (
          <label>
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              placeholder="you@example.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
        )}
        <label>
          Sensor
          <select value={deviceId} onChange={(event) => setDeviceId(event.target.value)}>
            <option value="all">All sensors</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn btn-primary" disabled={saving || !contactReady}>
          {saving ? 'Saving…' : channel === 'text' ? 'Save number' : 'Save email'}
        </button>
      </form>
      {savedContact && (
        <p className="alert-saved" role="status">
          Saved {savedContact}. You will get {savedChannel === 'text' ? 'a text' : 'an email'} when that sensor reaches Watch or Flooding, once sending is turned on.
        </p>
      )}
      {error && <p className="search-error" role="alert">{error}</p>}
    </main>
  );
};
